import { NJIconButton, NJToastContainer, NJToastService, NJButton, NJIcon } from '@engie-group/fluid-design-system-react';
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Flex } from "antd";
import './Generator.css';
import HeaderMenu from './components/HeaderMenu';
import InsightsPopup from './InsightsPopup';
import { useOktaAuth } from "@okta/okta-react";
import { useUser } from './context/UserContext';
import { useReferentials } from './context/ReferentialsContext';
import { apiGatewayConfig } from './config';
import { invokeLambda, displayNetworkError } from './api';
import { useLocation } from 'react-router-dom';
import JobAdDisplay from './components/JobAdDisplay/JobAdDisplay';
import "@engie-group/fluid-design-tokens/css";
import '@engie-group/fluid-design-system-react/css';
import LegalMesssage from './components/LegalMessage/LegalMessage';
import Loading from './Loading';
import AnalysisPanel from './AnalysisPanel'
import RefinePopup from './RefinePopup';
import FooterEngie from './components/Footer'
import { useModel } from './context/ModelContext';
import TranslateDropdown from './components/TranslateDropdown';
import ModalFeedback from './components/ModalFeedback';
import ModalRenameJobAd from './components/ModalRenameJobAd';
import SaveJobAdTemplate from './components/SaveJobAdTemplate';

const Generator = () => {

  const userInfo = useUser();
  const { oktaAuth } = useOktaAuth();
  const location = useLocation();
  const { referentials } = useReferentials();


  const [modelProvider] = useModel();
  const [isOpenRefinePopup, setIsOpenRefinePopup] = useState(false);
  const [isOpenInsightsPopup, setIsOpenInsightsPopup] = useState(false);
  const [isOpenFeedback, setIsOpenFeedback] = useState(false);
  const [isOpenRenameJobAdModal, setIsOpenRenameJobAdModal] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [generatedAd, setGeneratedAd] = useState('');
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [generatedEditedText, setGeneratedEditedText] = useState('');
  const [generatedEditedHtml, setGeneratedEditedHtml] = useState('');
  const [options, setOptions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [needRegenerate, setNeedRegenerate] = useState(false);
  const [genderAnalysis, setGenderAnalysis] = useState(null);
  const [jobAdInfo, setJobAdInfo] = useState(null);
  const [jobAdTitle, setJobAdTitle] = useState('');
  const [generatedTitle, setGeneratedTitle] = useState('');
  const [editTemplate, setEditTemplate] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {

    if ((window.location.hostname === 'localhost')) {
      setIsAdmin(true);
    } else {
      let username = userInfo?.preferred_username;
      if (username && referentials?.admin && userInfo) {
        let gid = username.split('@')[0].toUpperCase();
        setIsAdmin(referentials.admin.includes(gid));
      }
    }
  }, [referentials, userInfo]);

  useEffect(() => {
    const { action } = location.state || {};

    setEditTemplate(false);

    // New job ad: generate new job ad from job description and options
    if (action === 'newjobad') {
      const { options, jobDescription: jd } = location.state || {};

      // Check valid params
      if (!options || !jd) {
        // Redirect to the dashboard page
        window.location.href = '/dashboard';
        return;
      }
      setJobAdInfo(undefined);
      setOptions(options);
      setJobAdTitle('');
      setJobDescription(jd);

      // Generate the job ad
      setNeedRegenerate(true);
    }

    // Use an existing job ad
    if (action === 'jobad') {
      const { jobAdInfo: jai } = location.state || {};
      setJobAdInfo(jai);
      setOptions(jai.options);
      setJobAdTitle(jai.title);
      setJobDescription(jai.jobDescription);
      setGeneratedAd(jai.jobAdText);
    }

    // Use a template
    if (action === 'useTemplate') {
      const { jobAdInfo: jai } = location.state || {};
      setJobAdInfo(undefined);
      setJobAdTitle(jai.title);
      setOptions(jai.options);
      setJobDescription(jai.jobDescription);
      setGeneratedAd(jai.jobAdText);
    }

    // Edit a template
    if (action === 'editTemplate') {
      const { jobAdInfo: jai } = location.state || {};
      setJobAdInfo(jai);
      setJobAdTitle(jai.title);
      setOptions(jai.options);
      setJobDescription(jai.jobDescription);
      setGeneratedAd(jai.jobAdText);
      setEditTemplate(true);
    }

  }, [location.state]);

  const currentLanguage = useMemo(() => {
    return options.language || 'English';
  }, [options.language]);

  const formatTextWithStrongFirstLine = (inputText) => {
    let formattedText = inputText;
    const firstLineBreak = formattedText.indexOf('\n');
    if (firstLineBreak !== -1) {
      formattedText = formattedText.substring(0, firstLineBreak) + '</strong>' + formattedText.substring(firstLineBreak);
    }
    formattedText = '<strong>' + formattedText;
    return formattedText;
  };

  const formatTextShortTextAfterLineBreak = (inputText) => {
    const lines = inputText.split('\n'); // Split the input text into lines
    const formattedLines = lines.map((line, index) => {
      if (line.length < 50 && !line.startsWith('<strong>') && (index === 0 || lines[index - 1].trim() === ''))
        return line.startsWith('<strong>') ? line : `<strong>${line}</strong>`;
      return line; // Return the line unchanged
    });
    return formattedLines.join('\n'); // Join the lines back into a single string
  };

  const formatAddDivToLine = (inputText) => {
    const lines = inputText.split('\n'); // Split the input text into lines
    const formattedLines = lines.map((line, index) => {
      if (!line)
        return `<div><br></div>`
      return `<div>${line}</div>`; // Return the line unchanged
    });
    return formattedLines.join(''); // Join the lines back into a single string
  };

  const formatTitlesPrefixes = (inputText) => {
    const lines = inputText.split('\n'); // Split the input text into lines
    const formattedLines = lines.map((line, index) => {
      if (line.startsWith('# ')) return line.replace('# ', '');
      if (line.startsWith('## ')) return line.replace('## ', '');
      if (line.startsWith('### ')) return line.replace('### ', '');
      return line; // Return the line unchanged
    });
    return formattedLines.join('\n'); // Join the lines back into a single string
  };

  useEffect(() => {
    let formatted = generatedAd;
    formatted = formatTitlesPrefixes(formatted);
    formatted = formatTextWithStrongFirstLine(formatted);
    formatted = formatTextShortTextAfterLineBreak(formatted);
    formatted = formatAddDivToLine(formatted);

    // Replace CR LF with <br>
    // formatted = formatted.replace(/(\n)/g, '<p><br></p>'); // Replace line breaks with <br>

    setGeneratedEditedText(generatedAd);
    setGeneratedHtml(formatted);

    // Define title
    //if (!jobAdTitle) {
    const firstLineEndIndex = generatedAd.indexOf('\n');
    const title = firstLineEndIndex !== -1 ? generatedAd.substring(0, firstLineEndIndex) : generatedAd;
    //setJobAdTitle(title);
    setGeneratedTitle(title.length > 100 ? 'Untitled' : title);
    //}

  }, [generatedAd]);

  const adjustBackendGeneratedAd = useCallback((newAd) => {
    let text = newAd;
    // Remove ** 
    text = text.replace(/\*\*/g, '');
    return text;
  }, []);

  const handleGenerateJobAd = useCallback(async () => {
    // Prepare request
    const request = {
      job_description: jobDescription,
      persona: options.persona,
      profile: options.profile,
      seniority: options.seniority,
      globalEmployeeLevel: options.spc,
      experience: options.experience,
      job_family: options.jobFamily,
      tone: options.tone,
      gbu: options.gbu,
      countries: options.country,
      remote: options.remote,
      salary_range: options.salaryRange,
      currency: options.currency,
      language: currentLanguage,
      woman_recruitment: !!options.woman,
      provider: process.env.REACT_APP_JOB_AD_GENERATION_MODEL_PROVIDER.split('/')[0], //modelProvider.split('/')[0],
      model: process.env.REACT_APP_JOB_AD_GENERATION_MODEL_PROVIDER.split('/')[1], // modelProvider.split('/')[1],
    }
    setIsLoading(true);
    // Call Lambda
    setGeneratedAd('');

    try {
      const response = await invokeLambda(oktaAuth, apiGatewayConfig.endpointJobAdGeneration, request, userInfo);
      const statusCode = response.statusCode;
      setIsLoading(false);
      if (statusCode === 200) {
        const generatedText = adjustBackendGeneratedAd(response.inference);
        setGeneratedAd(generatedText);
      } else {
        console.error("Error:", response.body);
        alert(response.body);
      }

    } catch (error) {
      console.error('Error invoking Lambda:', error);
      displayNetworkError(error);
      setIsLoading(false);
    }
  }, [jobDescription, options, currentLanguage, modelProvider, oktaAuth, userInfo, adjustBackendGeneratedAd]);


  useEffect(() => {
    if (needRegenerate) {
      handleGenerateJobAd();
      setNeedRegenerate(false);
    }
  }, [needRegenerate, handleGenerateJobAd]);

  // Adjust jobAdTitle with generated title
  useEffect(() => {
    if (!jobAdTitle && generatedTitle) {
      setJobAdTitle(generatedTitle);
    }
  }, [generatedTitle, jobAdTitle]);

  const handleJobAdEdited = useCallback((newContent) => {
    setGeneratedEditedText(newContent.text);
    setGeneratedEditedHtml(newContent.html);
  }, []);


  const handleGenderBalance = async (moreWhat) => {
    // Prepare request
    const request = {
      gbu: options.gbu,
      language: currentLanguage,
      last_job_ad: generatedEditedText,
      //footer: lastFooter,
      gender_balance: moreWhat,
      analysis: genderAnalysis,
      provider: modelProvider.split('/')[0],
      model: modelProvider.split('/')[1],
    }

    setIsLoading(true);

    // Call Lambda

    try {
      const response = await invokeLambda(oktaAuth, apiGatewayConfig.endpointGenderBalance, request, userInfo);
      const statusCode = response.statusCode;
      //setLastApiResponse(response);
      setIsLoading(false);
      if (statusCode === 200) {
        const generatedText = adjustBackendGeneratedAd(response.inference);
        setGeneratedAd(generatedText);
      } else {
        console.error("Error:", response.body);
        alert(response.body);
      }

    } catch (error) {
      console.error('Error invoking Lambda:', error);
      displayNetworkError(error);
      //setLastApiResponse(error);
      setIsLoading(false);
    }
  };

  const handleFeedbackButton = (positive) => {
    if (positive) {
      handleFeedback('Up', '');
    }
    else {
      // Negative feedback
      setIsOpenFeedback(true);
    }
  }

  const handleFeedbackComment = (comment) => {
    setIsOpenFeedback(false);
    handleFeedback('Down', comment);
  }

  const handleFeedback = async (feedback, comment) => {
    // Prepare request
    const request = {
      name: jobAdTitle,
      feedback: feedback,
      comment: comment,
      job_description: jobDescription,
      job_ad: generatedEditedText,
      language: currentLanguage,
    }

    // Call Lambda
    try {
      await invokeLambda(oktaAuth, apiGatewayConfig.endpointJagFeedback, request, userInfo);
    } catch (error) {
      console.error('Error invoking Lambda:', error);
      displayNetworkError(error);
    } finally {
      NJToastService('Thank you for your feedback!', { iconName: 'check', shouldDismiss: true, dismissAfter: 2000 });
    }
  };

  const handleRefineJobAdd = async (refineRequest) => {
    // Prepare request
    const request = {
      job_description: jobDescription,
      gbu: options.gbu,
      language: currentLanguage,
      last_job_ad: generatedEditedText,
      //footer: lastFooter,
      user_demande: refineRequest,
      woman_recruitment: !!options.woman,
      provider: modelProvider.split('/')[0],
      model: modelProvider.split('/')[1],
    }

    setIsLoading(true);

    // Call Lambda

    try {
      const response = await invokeLambda(oktaAuth, apiGatewayConfig.endpointJobAdRefine, request, userInfo);
      const statusCode = response.statusCode;
      //setLastApiResponse(response);
      setIsLoading(false);
      if (statusCode === 200) {
        const generatedText = adjustBackendGeneratedAd(response.inference);
        setGeneratedAd(generatedText);
      } else {
        console.error("Error:", response.body);
        alert(response.body);
      }

    } catch (error) {
      console.error('Error invoking Lambda:', error);
      displayNetworkError(error);
      //setLastApiResponse(error);
      setIsLoading(false);
    }
  };

  const openRefineModal = () => {
    setIsOpenRefinePopup(true);
  }

  const handleRefineRequest = (request) => {
    setIsOpenRefinePopup(false);

    // Generate the job ad
    handleRefineJobAdd(request);
  }


  const handleSaveButton = ({ title, create }) => {

    if (create) {
      // Create template from job ad -> do not change title
      createJobAdInfo(title, true);
    } else {
      // Update title
      setJobAdTitle(title);
      if (jobAdInfo) {
        updateJobAdInfo(title);
      } else {
        createJobAdInfo(title, editTemplate);
      }
    }
  }

  const openInsightsModal = () => {
    setIsOpenInsightsPopup(true);
  }

  const handleInsightsChange = (options, newJobDescription) => {
    setIsOpenInsightsPopup(false);
    setOptions(options);
    setJobDescription(newJobDescription)

    // Generate the job ad
    setNeedRegenerate(true);
  }

  const updateGenderAnalysis = (analysis) => {
    setGenderAnalysis(analysis);
  };

  const handleUpdateCreateJobAdInfo = async (request) => {

    setIsLoading(true);

    // Call Lambda
    let returnedId;

    try {
      const response = await invokeLambda(oktaAuth, apiGatewayConfig.endpointJobAdInfoCreateUpdate, request, userInfo);
      setIsLoading(false);
      returnedId = response.id;
    } catch (error) {
      console.error('Error invoking Lambda:', error);
      displayNetworkError(error);
      //setLastApiResponse(error);
      setIsLoading(false);
    }

    return returnedId;
  };

  const updateJobAdInfo = (title) => {
    // Update jobAdInfo
    let request = {
      id: jobAdInfo.id,
      title: title,
      options: options,
      jobDescription: jobDescription,
      jobAdText: generatedEditedText,
      jobAdHtml: generatedEditedHtml
    };
    handleUpdateCreateJobAdInfo(request);
  }

  const createJobAdInfo = (title, isTemplate) => {
    // Create jobAdInfo
    let email = userInfo.username || userInfo.email;
    let user = email.split('@')[0].toUpperCase();

    let request = {
      user: isTemplate ? 'TEMPLATE' : user,
      title: title,
      options: options,
      jobDescription: jobDescription,
      jobAdText: generatedEditedText,
      jobAdHtml: generatedEditedHtml
    };
    let id = handleUpdateCreateJobAdInfo(request);
    if (id && !isTemplate) {
      setJobAdInfo(
        {
          ...jobAdInfo,
          id
        }
      )
    }
  }

  const onClickTranslate = (language) => {
    options.language = language;
    //handleTranslationJobAdd(language);
    // Generate the job ad
    setNeedRegenerate(true);
  };

  const handleTranslationJobAdd = async (language) => {
    // Prepare request
    const request = {
      job_description: jobDescription,
      gbu_evp: options.gbu,
      language: language,
      last_job_ad: generatedEditedText,
      //footer: lastFooter,
      //user_demande: refineDemand,
      provider: modelProvider.split('/')[0],
      model: modelProvider.split('/')[1],
    }

    setIsLoading(true);

    // Call Lambda

    try {
      const response = await invokeLambda(oktaAuth, apiGatewayConfig.endpointJobAdTranslation, request, userInfo);
      const statusCode = response.statusCode;
      setIsLoading(false);
      if (statusCode === 200) {
        const generatedText = adjustBackendGeneratedAd(response.inference);
        setGeneratedAd(generatedText);
      } else {
        console.error("Error:", response.body);
        alert(response.body);
      }

    } catch (error) {
      console.error('Error invoking Lambda:', error);
      displayNetworkError(error);
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Loading text="Please wait, Job ad is being generated..." />}
      <InsightsPopup
        isOpen={isOpenInsightsPopup}
        handleClose={() => setIsOpenInsightsPopup(false)}
        handleValidate={handleInsightsChange}
        insightsOptions={options}
        jobDescription={jobDescription}
        editJobDescription={true}
      />
      <RefinePopup
        isOpen={isOpenRefinePopup}
        handleClose={() => setIsOpenRefinePopup(false)}
        handleValidate={handleRefineRequest}
      />
      <ModalFeedback
        isOpen={isOpenFeedback}
        handleFeedback={handleFeedbackComment}
        handleCancel={() => setIsOpenFeedback(false)}
      />
      <ModalRenameJobAd
        isOpen={isOpenRenameJobAdModal}
        title={jobAdTitle}
        isTemplate={editTemplate}
        handleCancel={() => { setIsOpenRenameJobAdModal(false) }}
        handleRename={(id, newTitle) => { setIsOpenRenameJobAdModal(false); setJobAdTitle(newTitle) }}
      />

      <div className='sizelimit-outer'>
        <div className='sizelimit-inner sizelimit-max-width'>

          <div className="generator-container">
            <Flex vertical={true} justify='space-between' style={{ height: '100%' }}>


              <div className="dashboard-header">
                <HeaderMenu
                  activeItemId='dashboard'
                  username={userInfo?.name}
                />
              </div>

              <div className="generator-legal-message">
                <LegalMesssage />
              </div>


              <Flex gap={0} justify='space-between' className="generator-content">

                <div className="generator-left-panel">
                  <NJToastContainer type="full-width" />

                  <Flex gap={0} justify='space-between' align='center' className="generator-title-language">
                    <Flex gap={12} justify='space-between' align='center' className='generator-job-title'>
                      {editTemplate && <>
                        <NJIcon
                          name="content_paste"
                          size="md"
                        />
                      </>}
                      {jobAdTitle}
                      <NJIconButton
                        icon="edit"
                        label="Edit job title"
                        type="button"
                        variant="brand"
                        iconClassName="material-icons-outlined"
                        onClick={() => {
                          setIsOpenRenameJobAdModal(true);
                        }}
                      />
                    </Flex>
                    <div>
                      <TranslateDropdown
                        languageItems={referentials?.languages.map(item => { return { label: item, key: item } }) || []}
                        onSelect={onClickTranslate}
                        currentLanguage={currentLanguage}
                      />
                    </div>
                  </Flex>

                  {/* Generated text */}
                  <JobAdDisplay
                    text={generatedAd}
                    html={generatedHtml}
                    notifyTextChanged={handleJobAdEdited}
                  />

                  <div className="generator-actions">
                    <Flex justify="space-between" align="center" className="nj-margin-top-2">

                      <Flex gap={20} justify="start" align="center" >
                        <NJButton
                          label="Rework the Ad"
                          emphasis="subtle"
                          variant="ai"
                          icon="engie_ai"
                          onClick={openRefineModal}
                        />
                        <NJButton
                          label="Update Filters"
                          emphasis="subtle"
                          variant="ai"
                          icon="edit"
                          iconClassName="material-icons-outlined"
                          onClick={openInsightsModal}
                        />
                      </Flex>


                      <Flex gap={20} justify="end" align="center" >
                        <SaveJobAdTemplate
                          jobAdTitle={jobAdTitle}
                          isTemplate={editTemplate}
                          allowSaveTemplate={isAdmin}
                          handleSave={handleSaveButton}
                        />
                        <NJButton
                          label="Copy the Ad"
                          variant="primary"
                          icon="content_copy"
                          emphasis="bold"
                          /* className='generator-clipboard' */
                          onClick={(e) => {
                            e.preventDefault();
                            // Copy to clipboard as html and text
                            const clipboardItem = new ClipboardItem({
                              'text/html': new Blob([generatedEditedHtml], { type: 'text/html' }),
                              'text/plain': new Blob([generatedEditedText], { type: 'text/plain' })
                            });
                            navigator.clipboard.write([clipboardItem])
                              .then(() => NJToastService('Ad copied to clipboard!', { iconName: 'check', shouldDismiss: true, dismissAfter: 2000 }))
                              .catch(err => console.error('Error copying text: ', err));
                          }}
                        />
                      </Flex>
                    </Flex>

                  </div>

                </div>
                <div className="generator-right-panel">
                  <AnalysisPanel
                    firstAd={generatedAd}
                    editedAd={generatedEditedText}
                    updateGenderAnalysis={updateGenderAnalysis}
                    handleGenderBalance={handleGenderBalance}
                    handleFeedbackButton={handleFeedbackButton}
                    language={currentLanguage}
                  />
                </div>
              </Flex>


              <FooterEngie />
            </Flex>
          </div>
        </div>
      </div>
    </>
  )
}
export default Generator;